import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ComposedChart, Bar, Line, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { gridLine, axisTick, tooltipProps } from "@plannel/components/dashboard/chartTokens";
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";

/*
 * DW 대시보드 공용 차트 스캐폴드.
 * 마진·그리드·축·툴팁 기본값을 여기서 소유해 카드마다 복붙되지 않게 한다.
 * 레퍼런스: pages/dashboard/SalesStatusDashboard.js
 */

/*
 * DW 대시보드 차트 타입 — 설정 모달(ChartConfigModal chartTypes)에 그대로 넘긴다.
 * barOrLine 이 렌더할 수 있는 타입과 **같은 목록**이어야 한다(여기가 단일 출처).
 *   DASHED = 점선. 목표·안전재고·총능력처럼 "기준선" 성격 시리즈를 실적선과 구분할 때 쓴다.
 * 값을 추가하면 i18n 에 **소문자 키**가 있어야 한다(MeasureConfigList 가 t(값.toLowerCase()) 로 라벨을 찾는다).
 * ⚠ AREA 를 허용하는 카드는 {areaGradientDefs([...])} 에 해당 시리즈를 반드시 등록할 것 —
 *   빠지면 url(#dwArea-..) 참조가 깨져 면이 검게 칠해진다.
 */
export const DW_CHART_TYPES = ["BAR", "LINE", "AREA", "DASHED"];

// 점선 대시 패턴 — 앱 전역에서 기준선 표기를 통일하려고 여기 고정한다.
const DASH_PATTERN = "4 3";

/**
 * 시리즈 하나를 chartConfig 의 chartType(BAR/LINE/AREA/DASHED)에 따라 렌더.
 *
 * recharts 는 children 의 타입(Bar/Line)을 직접 검사하므로 래퍼 컴포넌트로 감싸면 안 된다.
 * → 컴포넌트가 아니라 **엘리먼트를 반환하는 함수**로 유지한다.
 *
 * @param chartConfig z_p13n 차트 설정 { [key]: { chartType, activeFlg } }
 * @param spec { key, dataKey, name, color, yAxisId, dflt, stackId, barSize }
 *        dflt   : 설정에 없을 때 기본 타입
 *        stackId: 같은 값을 준 시리즈끼리 누적(BAR·AREA 만 해당 — LINE/DASHED 는 쌓을 수 없어 무시된다).
 *                 스택 구성원을 개별로 LINE 으로 바꾸면 그 시리즈만 스택에서 빠져나온다(의도된 동작).
 */
// area 시리즈 그라데이션 id 규약 — barOrLine(AREA)/AreaGradientDefs 가 공유.
//   dataKey 에 공백/특수문자가 있으면 url(#..) 참조가 깨져 SVG 가 검정으로 칠해지므로 id-safe 로 정규화.
export const dwAreaId = (dataKey) => `dwArea-${String(dataKey).replace(/[^\w-]/g, "_")}`;

/**
 * area 시리즈용 그라데이션 defs — `<DashboardComposedChart>` 안에서 **함수로 호출**한다:
 *   {areaGradientDefs([{ dataKey, color }, ...])}
 *
 * ★ 컴포넌트가 아니라 함수인 이유 — `barOrLine` 과 **완전히 같은 이유**다.
 *   recharts 의 `renderByOrder` 는 자식을 이렇게 거른다:
 *     · `isSvgElement(child)` = **type 이 문자열인 raw SVG 태그**('defs' 등) → 렌더
 *     · 아니면 `renderMap[displayName]` 조회 → 핸들러가 없으면 **조용히 버린다**
 *   그래서 `<AreaGradientDefs items={...} />` 처럼 컴포넌트로 감싸면 recharts 가 통째로 버리고,
 *   `url(#dwArea-..)` 가 가리킬 그라데이션이 없어 **면이 안 칠해진다**(브라우저에 따라 검게 나오기도 한다).
 *   예전엔 컴포넌트였고, 그래서 AREA 로 바꾼 시리즈가 조용히 비어 보였다(2026-08-06 수정).
 *
 * @param items [{ dataKey, color }] — 그 카드에서 AREA 가 될 수 있는 시리즈 **전부**
 */
export const areaGradientDefs = (items = []) => (
  <defs>
    {items.map(({ dataKey, color }) => (
      <linearGradient key={dataKey} id={dwAreaId(dataKey)} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity={0.28} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    ))}
  </defs>
);

export const barOrLine = (chartConfig, { key, dataKey, name, color, yAxisId, dflt = "BAR", stackId, barSize = 10 }) => {
  const type = String(chartConfig?.[key]?.chartType ?? dflt).toUpperCase();
  if (type === "AREA") {
    return (
      <Area key={dataKey} yAxisId={yAxisId} stackId={stackId} type="monotone" dataKey={dataKey} name={name}
        stroke={color} strokeWidth={2} fill={`url(#${dwAreaId(dataKey)})`}
        dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
    );
  }
  if (type === "LINE" || type === "DASHED") {
    return (
      <Line key={dataKey} yAxisId={yAxisId} type="monotone" dataKey={dataKey} name={name}
        stroke={color} strokeWidth={1.8} strokeDasharray={type === "DASHED" ? DASH_PATTERN : undefined}
        dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
    );
  }
  return (
    <Bar key={dataKey} yAxisId={yAxisId} stackId={stackId} dataKey={dataKey} name={name}
      fill={color} barSize={barSize} radius={[2, 2, 0, 0]} />
  );
};

/**
 * ComposedChart 셸 — 높이를 부모(flex)에 맞추고 축/그리드/툴팁 기본값을 적용.
 *
 * @param yAxes [{ id?, orientation?, domain?, tickFormatter }]
 *        id 를 주면 yAxisId 로 렌더(다축). 생략하면 단일 축(시리즈도 yAxisId 없이).
 *        domain 은 축 범위 고정용 — 비율(%) 축처럼 0~100 을 항상 보여야 눈금 비교가 되는 축에 쓴다.
 * @param children 시리즈 엘리먼트들(barOrLine 결과 등)
 */
export const DashboardComposedChart = ({
  data, xKey = "period", yAxes = [], tooltipFormatter, children, ...chartProps
}) => {
  const theme = useTheme();
  const tick = axisTick(theme);
  return (
    <Box sx={{ flex: 1, minHeight: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }} {...chartProps}>
          <CartesianGrid strokeDasharray="3 4" stroke={gridLine(theme)} vertical={false} />
          <XAxis dataKey={xKey} tick={tick} />
          {yAxes.map((ax, i) => (
            <YAxis
              key={ax.id ?? i}
              {...(ax.id ? { yAxisId: ax.id } : {})}
              {...(ax.orientation ? { orientation: ax.orientation } : {})}
              {...(ax.domain ? { domain: ax.domain } : {})}
              tick={tick}
              tickFormatter={ax.tickFormatter}
            />
          ))}
          <Tooltip {...tooltipProps()} formatter={tooltipFormatter} content={<AgStyleTooltipContent />} />
          {children}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};
